import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { CategoryForm } from '@/components/categories/CategoryForm';
import { useCategories, useCreateCategory } from '@/hooks/useCategories';
import { useTranslation } from '@/i18n';
import type { Budget, CreateBudgetInput } from '@/types/budget';
import type { Category, CreateCategoryInput } from '@/types/category';

interface BudgetFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateBudgetInput) => void;
  budget?: Budget | null;
  isLoading?: boolean;
}

export const BudgetForm: React.FC<BudgetFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  budget,
  isLoading = false,
}) => {
  const { t, locale } = useTranslation();
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories();
  const createCategoryMutation = useCreateCategory();
  const categories = categoriesData?.categories || [];
  const [showCategoryForm, setShowCategoryForm] = useState(false);

  const handleCreateCategory = async (data: CreateCategoryInput) => {
    const newCategory = await createCategoryMutation.mutateAsync(data);
    setShowCategoryForm(false);
    setFormData((prev) => ({ ...prev, categoryId: newCategory.id }));
  };

  // Get localized category name
  const getCategoryName = (category: Category): string => {
    if (locale === 'pt-BR') {
      return category.name;
    }
    return category.nameEn || category.name;
  };

  // Format currency based on locale
  const formatCurrency = (value: number) => {
    const currencyMap: Record<string, string> = {
      'pt-BR': 'BRL',
      'en-GB': 'GBP',
      'es-ES': 'EUR',
      'fr-FR': 'EUR',
      'it-IT': 'EUR',
      'ar': 'SAR',
      'zh-CN': 'CNY',
    };
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyMap[locale] || 'GBP',
    }).format(value);
  };

  const [formData, setFormData] = useState<CreateBudgetInput>({
    name: '',
    categoryId: null,
    amount: 0,
    period: 'monthly',
    alertThreshold: 80,
  });

  useEffect(() => {
    if (budget) {
      setFormData({
        name: budget.name,
        categoryId: budget.categoryId,
        amount: budget.amount,
        period: budget.period,
        alertThreshold: budget.alertThreshold,
      });
    } else {
      setFormData({
        name: '',
        categoryId: null,
        amount: 0,
        period: 'monthly',
        alertThreshold: 80,
      });
    }
  }, [budget, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={budget ? t.app.budgets.form.editBudget : t.app.budgets.form.newBudget}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name */}
        <Input
          label={t.app.budgets.form.budgetName}
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder={t.app.budgets.form.budgetNamePlaceholder}
          required
        />

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t.app.budgets.form.category}
          </label>
          <div className="flex gap-2">
            <Select
              value={formData.categoryId || ''}
              onChange={(e) =>
                setFormData({ ...formData, categoryId: e.target.value || null })
              }
              disabled={categoriesLoading}
              options={[
                { value: '', label: categoriesLoading ? t.app.common.loading : t.app.budgets.form.allExpenses },
                ...categories.map((c) => ({ value: c.id, label: getCategoryName(c) })),
              ]}
              className="flex-1"
            />
            <button
              type="button"
              onClick={() => setShowCategoryForm(true)}
              className="p-2 rounded-lg border border-gray-300 dark:border-secondary-600 hover:bg-gray-100 dark:hover:bg-secondary-700 transition-colors"
              title={t.app.common.create}
            >
              <Plus className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Amount */}
        <Input
          label={t.app.budgets.form.limit}
          type="number"
          min={0}
          step={0.01}
          value={formData.amount || ''}
          onChange={(e) =>
            setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })
          }
          placeholder="0.00"
          required
        />

        {/* Period */}
        <Select
          label={t.app.budgets.form.period}
          value={formData.period}
          onChange={(e) =>
            setFormData({ ...formData, period: e.target.value as 'monthly' | 'yearly' })
          }
          options={[
            { value: 'monthly', label: t.app.budgets.period.monthly },
            { value: 'yearly', label: t.app.budgets.period.yearly },
          ]}
        />

        {/* Alert Threshold */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {t.app.budgets.form.alertThreshold}
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={50}
              max={100}
              value={formData.alertThreshold}
              onChange={(e) =>
                setFormData({ ...formData, alertThreshold: parseInt(e.target.value) })
              }
              className="flex-1"
            />
            <span className="text-sm font-medium text-gray-900 dark:text-white w-12 text-center">
              {formData.alertThreshold}%
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t.app.budgets.form.alertDescription}
          </p>
        </div>

        {/* Preview */}
        <div className="p-4 bg-gray-50 dark:bg-secondary-900 rounded-lg">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t.app.budgets.form.preview}:</p>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">
                {formData.name || t.app.budgets.form.budgetName}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {formData.period === 'monthly' ? t.app.budgets.period.monthly : t.app.budgets.period.yearly}
              </p>
            </div>
            <p className="text-lg font-bold text-primary-600 dark:text-primary-400">
              {formatCurrency(formData.amount || 0)}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-secondary-700">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t.app.common.cancel}
          </Button>
          <Button type="submit" isLoading={isLoading}>
            {budget ? t.app.budgets.form.save : t.app.budgets.form.create}
          </Button>
        </div>
      </form>

      {/* Create Category Modal */}
      <CategoryForm
        isOpen={showCategoryForm}
        onClose={() => setShowCategoryForm(false)}
        onSubmit={handleCreateCategory}
        isLoading={createCategoryMutation.isPending}
      />
    </Modal>
  );
};

export default BudgetForm;
